import React, { createContext, useContext, useEffect, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { isAndroidBoldTextEnabled } from 'android-bridge';

/**
 * Isolated context for the one bridge value read on a hot path: every Android
 * `<Text>` subscribes to it (via `AndroidBoldTextAccessibilitySpacer`). It carries a
 * bare boolean that only flips when the system Bold text setting is toggled, so the
 * whole `<Text>` tree is never re-rendered by unrelated state.
 */
const AndroidBoldTextContext = createContext<boolean>(false);

/**
 * Whether Android's system **Bold text** setting is on. Safe to read in
 * high-frequency components like `Text`. Always `false` on iOS and web.
 */
export function useAndroidBoldText(): boolean {
  return useContext(AndroidBoldTextContext);
}

/**
 * Provides `useAndroidBoldText()` to descendants. Mount near the app root
 * (`_layout.tsx`). On Android the value refreshes on mount and whenever the app
 * becomes active again, so toggling the system setting is picked up without a
 * restart. No-op on iOS and web — the context stays `false`.
 *
 * Adding more bridge values later? Don't widen this boolean context — the whole
 * `<Text>` tree subscribes to it. Add a SEPARATE context + hook for the new value
 * so hot paths stay isolated, e.g.:
 *
 *   const AndroidConfigContext = createContext<IAndroidConfigState>(defaultConfig);
 *   export const useAndroidConfig = () => useContext(AndroidConfigContext);
 *   // read native values into per-field useState, memoize the value object, then
 *   // nest <AndroidConfigContext.Provider> below this one.
 *
 * Only put foreground-stable settings in any UI-consumed context; expose
 * volatile/high-frequency values (battery, network, sensors) imperatively.
 */
export function AndroidBridgeProvider({ children }: { children: React.ReactNode }) {
  const [isBoldTextEnabled, setIsBoldTextEnabled] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'android') {
      return;
    }

    const refresh = () => {
      setIsBoldTextEnabled(isAndroidBoldTextEnabled());
    };

    refresh();

    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        refresh();
      }
    });

    return () => subscription.remove();
  }, []);

  return (
    <AndroidBoldTextContext.Provider value={isBoldTextEnabled}>
      {children}
    </AndroidBoldTextContext.Provider>
  );
}
